/**
 * 「错词本」的**纯展示逻辑**（可测）。
 *
 * 这一层不碰网络：它把后端给的错词行翻译成家长看得懂的话。
 *
 * ## 排程规则的人话**不在这里**
 *
 * "错的词 1 天 → 3 天 → 7 天再来、连对 2 次算掌握"这句话由**后端**给
 * （`GET .../mistakes` 的 `ladderNote`，唯一出处是
 * `packages/shared/src/dictation/schedule.ts` 的 `describeReviewLadder()`）。
 *
 * 前端**刻意不自己再写一份**：批改页与错词本上出现的规则说明必须逐字一致，
 * 否则家长会以为两处说的是两套规则 —— 而"系统是不是在偷偷乱排"这个问题
 * 一旦冒出来，这个功能（"系统性重听"）就失去信任了。
 * 所以这里只在需要时**转发**后端那一句，拿不到就说"没拿到"。
 */
import type { MistakeView } from './api/dictationSessions';
import { countLabel } from './api/dictation';

/** 错的次数 → 人话（两种错法分开说 —— 它们对应两种辅导方式）。 */
export function describeMistakeCounts(mistake: {
  wrongCount: number;
  missingCount: number;
}): string {
  const parts: string[] = [];
  if (mistake.wrongCount > 0) parts.push(`写错 ${mistake.wrongCount} 次`);
  if (mistake.missingCount > 0) parts.push(`没写出来 ${mistake.missingCount} 次`);
  return parts.length === 0 ? '—' : parts.join(' · ');
}

/**
 * 一条错词的"下次复习" → 人话。
 *
 * ⚠️ 三种情况必须分开说：
 *   · 已掌握（`resolvedAt !== null`）—— 不再排复习，并说清是**谁**判的
 *     （自动 = 连续答对；手动 = 家长标的）；
 *   · 已到期（`due === true`）—— "今天该复习"；
 *   · 还没到期 —— 说清"排在第几天档"。
 *
 * `due === null`（字段没拿到）时说「—」而**不是**"没到期"：
 * 把拿不到当成"没到期"会让家长以为系统漏排了。
 */
export function describeMistakeDue(mistake: MistakeView): string {
  if (mistake.resolvedAt !== null) {
    return mistake.resolvedBy === 'manual' ? '已掌握（家长标记）' : '已掌握（连续答对）';
  }
  if (mistake.due === null) return '—';
  if (mistake.due) return '今天该复习';
  return `排在第 ${mistake.reviewIntervalDays} 天档`;
}

/** 错词本的顶部摘要（"还没掌握 7 个词，其中今天该复习 3 个"）。 */
export function describeMistakeHeader(
  openCount: number | null,
  dueCount: number | null,
  total: number | null,
): string {
  if (openCount === null) {
    return '没拿到错词本的统计（与"一个错词都没有"是两件事）。';
  }
  if (openCount === 0) return '错词本里还没有词 —— 批改一次听写，错的词会自动进来。';
  return `还没掌握 ${countLabel(openCount)} 个词，其中今天该复习 ${countLabel(
    dueCount,
  )} 个${total !== null && total !== openCount ? `（当前筛选显示 ${total} 个）` : ''}。`;
}

/**
 * 排序：**今天该复习的排最前**，然后按下次复习时间，最后按词面。
 *
 * 后端的顺序已经是"按 `next_review_at` 升序"，所以这里只在**前端自己的筛选**
 * （例如家长勾了"只看今天该复习的"）之后重新排一次，保证"最上面的就是最该练的"。
 *
 * ⚠️ 用 `.slice()` 复制：直接 `sort` 会就地改 react-query 缓存里的那个数组
 *   （那是共享的状态，改了会让别处的列表顺序莫名其妙地变）。
 */
export function sortMistakes(mistakes: readonly MistakeView[]): MistakeView[] {
  return mistakes.slice().sort((a, b) => {
    const aDue = a.due === true ? 1 : 0;
    const bDue = b.due === true ? 1 : 0;
    if (aDue !== bDue) return bDue - aDue;
    const aNext = a.nextReviewAt ?? '9999';
    const bNext = b.nextReviewAt ?? '9999';
    if (aNext !== bNext) return aNext < bNext ? -1 : 1;
    return a.wordText.localeCompare(b.wordText, 'zh-Hans-CN');
  });
}

/** 可以用来发给后端的词 id（词被删过的行没有 `wordId`，不能参与动作）。 */
export function actionableWordIds(mistakes: readonly MistakeView[]): string[] {
  return mistakes
    .map((mistake) => mistake.wordId)
    .filter((id): id is string => id !== null && id !== '');
}
